import { AlertCircle, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import type { PolicyViolation } from '@/data/mockPolicyViolations';

// ─── F-02 helper component ─────────────────────────────────────────────────
// Renders the list of anomaly + policy-violation issues inside the flagged-
// approval confirmation dialog.
export function FlaggedIssuesList({
  anomalyResult,
  policyViolations,
}: {
  anomalyResult: AnomalyDetectionResult | undefined;
  policyViolations: PolicyViolation[];
}) {
  const hasAnomalies = anomalyResult?.hasAnomalies && (anomalyResult.anomalies?.length ?? 0) > 0;
  const hasViolations = policyViolations.length > 0;
  if (!hasAnomalies && !hasViolations) return null;

  return (
    <div className="my-2 max-h-[260px] overflow-y-auto space-y-3">
      {hasAnomalies && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Detected Anomalies
          </div>
          <div className="space-y-1.5">
            {anomalyResult!.anomalies.map((anomaly, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
              >
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{anomaly.message}</span>
                  {anomaly.severity && (
                    <Badge
                      variant="outline"
                      className={`ml-2 text-xs ${
                        anomaly.severity === 'high'
                          ? 'border-red-400 text-red-700 dark:text-red-400'
                          : anomaly.severity === 'medium'
                          ? 'border-amber-400 text-amber-700 dark:text-amber-400'
                          : 'border-muted-foreground text-muted-foreground'
                      }`}
                    >
                      {anomaly.severity}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {hasViolations && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            Policy Violations
          </div>
          <div className="space-y-1.5">
            {policyViolations.map((violation, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-sm p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800"
              >
                <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-600" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{violation.message}</span>
                  {violation.severity && (
                    <Badge
                      variant="outline"
                      className={`ml-2 text-xs ${
                        violation.severity === 'high'
                          ? 'border-red-400 text-red-700 dark:text-red-400'
                          : violation.severity === 'medium'
                          ? 'border-amber-400 text-amber-700 dark:text-amber-400'
                          : 'border-muted-foreground text-muted-foreground'
                      }`}
                    >
                      {violation.severity}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
